import { ExpirationDateTimeComponent } from "@goodfill/domain-properties-components";
import { TimeInForce } from "@goodfill/models-typescript";
import {
  Flex,
  Span,
  InputNumber,
  InputNumberFormatter,
  InputDate,
  CalendarProps,
  Dropdown,
  DropdownItem,
} from "@goodfill/ui-components";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { FormValidationResult } from "../../../../form/formValidation";
import { FormValidationComponent } from "../../../../form/FormValidationComponent";
import { Popover } from "../../../../ui-components/tooltip/Popover";
import { InfoTooltip } from "../../../../ui-components/tooltip/InfoTooltip";
import "./CreateOrderExpirationDateInput.scss";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";

interface Props {
  timezone: string;
  disabled: boolean;
  timeInForce: TimeInForce;
  onChange: (value: DateTime) => void;
  priceVsYield: "price" | "yield";
  validation: FormValidationResult;
  value: DateTime;
  showLabel?: boolean;
  errorInTooltip?: boolean;
}

const padNumberLeft: InputNumberFormatter = (value) => {
  if (typeof value !== "number") {
    return "";
  }
  return String(value).padStart(2, "0");
};

enum AmOrPm {
  AM = "AM",
  PM = "PM",
}

export const CreateOrderExpirationDateInput: React.FC<Props> = ({
  timezone,
  disabled,
  onChange,
  timeInForce,
  validation,
  value,
  showLabel,
  errorInTooltip,
}) => {
  const zonedValue = value.setZone(timezone);

  const border = validation.length > 0 ? "red" : "neutral";

  const [amOrPm, setAmOrPm] = useState(zonedValue.toFormat("a") as AmOrPm);

  const setSelectedDay = useCallback<CalendarProps["setSelectedDay"]>(
    (day) => {
      const newValue = DateTime.fromISO(`${day}T00:00:00`, {
        zone: zonedValue.zoneName,
      }).set({
        hour: zonedValue.hour,
        minute: zonedValue.minute,
      });
      onChange(newValue);
    },
    [onChange, zonedValue],
  );

  const shouldDisplayTime = timeInForce === TimeInForce.CustomToday || timeInForce === TimeInForce.CustomFuture;

  const shouldDisplayCalendar = timeInForce === TimeInForce.CustomFuture;

  const [timeDropdownIsOpen, setTimeDropdownIsOpen] = useState(false);

  function setTimeFormatting(time: AmOrPm) {
    if (time === amOrPm) return;

    const newValue = zonedValue.set({ hour: zonedValue.hour + (time === AmOrPm.PM ? 12 : -12) });

    setAmOrPm(time);
    setTimeDropdownIsOpen(false);
    onChange(newValue);
  }

  const changeHours = (hour: number | null) => {
    console.log(hour);

    if (hour === null) return;
    if (hour <= 12) {
      const newValue = zonedValue.set({ hour: hour + (amOrPm === AmOrPm.PM ? 12 : 0) });
      console.log("newValue", newValue.toISO());

      onChange(newValue);
    }
  };

  const changeMinute = (minute: number | null) => {
    if (minute === null) return;
    const newValue = zonedValue.set({ minute });
    onChange(newValue);
  };

  return (
    <div className="flex justify-start items-center">
      <Flex direction="col" justify="start" items="center">
        <Flex direction="rowCol" gap={2} width="w-full">
          {shouldDisplayCalendar && (
            <Flex direction="col" justify="between">
              {showLabel !== false ? (
                <Span size="base" font="semibold" className="px-0-important py-2">
                  <Popover
                    content={
                      <div>
                        <p className="mb-0-important px-4 py-4 text-white">
                          The expiration date is entered in the EST/EDT time zone. Under date picker you can see the
                          value of the selected expiration date in your current time zone.
                        </p>
                      </div>
                    }
                  />
                  Order Expiration Date
                </Span>
              ) : null}
              <InputDate
                className="input-date"
                border={border}
                value={zonedValue.toFormat("yyyy-MM-dd") ?? ""}
                setValue={setSelectedDay}
              />
              {shouldDisplayTime ? (
                <Span size="sm" neutral={true} className="expiry-date-fonts block">
                  &nbsp;
                </Span>
              ) : null}
            </Flex>
          )}
          {shouldDisplayTime ? (
            <Flex direction="col" justify="start">
              {showLabel !== false ? (
                <Span size="base" font="semibold" className="px-0-important py-2">
                  <InfoTooltip
                    text={
                      "The expiration date is entered in the EST/EDT time zone. Under date picker you can see the value of the selected expiration date in your current time zone."
                    }
                  />
                  Order Expiration Time
                </Span>
              ) : null}

              <Flex gap={2} items="end" justify="start">
                <InputNumber
                  disabled={disabled}
                  value={zonedValue.hour - (amOrPm === AmOrPm.PM ? 12 : 0)}
                  step={1}
                  small
                  setValue={changeHours}
                  min={1}
                  max={12}
                  formatValue={padNumberLeft}
                  className="input-time"
                  border={border}
                />
                <InputNumber
                  disabled={disabled}
                  small
                  step={1}
                  value={zonedValue.minute}
                  setValue={changeMinute}
                  min={0}
                  max={59}
                  className="input-time"
                  formatValue={padNumberLeft}
                  border={border}
                />
                <Dropdown
                  isOpen={timeDropdownIsOpen}
                  setIsOpen={setTimeDropdownIsOpen}
                  id="time"
                  trigger={<h1 className="cursor-pointer">{amOrPm}</h1>}
                >
                  <div className="flex">
                    <DropdownItem onClick={() => setTimeFormatting(AmOrPm.AM)}>
                      <div>{AmOrPm.AM}</div>
                    </DropdownItem>
                    <DropdownItem onClick={() => setTimeFormatting(AmOrPm.PM)}>
                      <div>{AmOrPm.PM}</div>
                    </DropdownItem>
                  </div>
                </Dropdown>
              </Flex>
              <Span size="sm" neutral={true} className="expiry-date-fonts block">
                <ExpirationDateTimeComponent value={zonedValue} showTimezone={true} timezone={TIMEZONE_NEY_YORK} />
              </Span>
            </Flex>
          ) : null}
        </Flex>
        <Flex className="expiration-input-error-boundary" width="w-full">
          {errorInTooltip === false || errorInTooltip === undefined ? (
            <FormValidationComponent validation={validation} className="px-0-important" />
          ) : null}
        </Flex>
      </Flex>
      {errorInTooltip === true ? <FormValidationComponent validation={validation} tooltip /> : null}
    </div>
  );
};
